import { useGetReviewsSummaryQuery } from '@/client/graphql/reviews.generated';
import { FEATURE_FLAGS } from '@/config/feature-flags';
import { Box, Badge, Heading, Text, Flex, Grid, GridItem } from '@chakra-ui/react';
import { useComponentSlots } from '@meritcircle/ui';
import { FullWidthImage, PulseLoader, Rating } from '@meritcircle/ui/components';
import { rem } from 'polished';
import { FC } from 'react';
import { GameOverviewHeroProps } from './game-overview-hero.types';

const GameOverviewHero: FC<GameOverviewHeroProps> = ({
  badges,
  tokens,
  title,
  titleLevel = 'h1',
  summary,
  children,
  heroImageUrl,
  gameSlug,
}) => {
  const [Slot] = useComponentSlots(children);

  const [{ data, fetching }] = useGetReviewsSummaryQuery({
    pause: !FEATURE_FLAGS.REVIEWS,
    variables: {
      gameSlug: gameSlug,
    },
  });

  const averageRating = data?.getReviewsSummary.averageRating || 0;

  return (
    <Box as="section" overflow="hidden">
      <FullWidthImage backgroundImage={heroImageUrl} wrapperProps={{ maxW: undefined }}>
        <Grid gridTemplateColumns={{ base: 'repeat(4, 1fr)', md: 'repeat(16, 1fr)' }}>
          <GridItem colSpan={{ base: 4, md: 12, lg: 8 }}>
            <Flex mb="1rem" flexWrap="wrap">
              {badges &&
                !!badges.length &&
                badges.map(badge => (
                  <Badge
                    mr="sm"
                    mb="sm"
                    variant={badge.toLowerCase() === 'new' ? 'arcticBlue' : undefined}
                    key={badge}>
                    {badge}
                  </Badge>
                ))}
              {tokens &&
                !!tokens.length &&
                tokens.map(token => (
                  <Badge mr="sm" mb="sm" key={token}>
                    {token}
                  </Badge>
                ))}
            </Flex>
            <Heading
              size="h1"
              as={titleLevel}
              variant="glowing"
              wordBreak={{ base: 'break-word', md: 'initial' }}
              sx={{ wordWrap: { base: 'normal' } }}
              mb=".8rem">
              {title}
            </Heading>
            {FEATURE_FLAGS.REVIEWS && (
              <Flex mb="md" minH={rem(14)} alignItems="center">
                {fetching ? (
                  <PulseLoader />
                ) : (
                  <Rating rating={averageRating} showOnlyStars={!averageRating} />
                )}
              </Flex>
            )}
            <Text fontSize={'md'}>{summary}</Text>

            <Slot name="buttons" />
          </GridItem>
        </Grid>
      </FullWidthImage>
    </Box>
  );
};

export default GameOverviewHero;
